import * as THREE from 'three';
import Experience from '../Experience.js';
import Table from './Table.js';
import Card from './Card.js';
import Light from './Light.js';
import cloneSceneAndMaterials from '../Utils/cloneSceneAndMaterials.js';
import Zone from './Zone.js';
import Animations from '../Systems/Animations.js';

export default class Space {

    static CARD_DOWN_Z = 0.025;
    static CARD_LIFTED_Z = 0.5;
    static CARD_HIGHLIFTED_Z = 2;
    static CARD_LIFTED_REMOTE_Z = 0.25;
    static CARD_LIFTED_REMOTE_ROTATION_FACTOR = this.CARD_LIFTED_REMOTE_Z / this.CARD_LIFTED_Z;
    static CARD_FOCUSED_POSITION = new THREE.Vector3(0, -1.1, 2.5);
    static CARD_FOCUSED_ROTATION_X = 0.36;
    static CARD_DEFAULT_ROTATION_X = 0;

    static ZONE_GROUP_POS = new THREE.Vector2(-3, .7);

    constructor(zonesLayout) {
        this.experience = new Experience();
        this.eventBus = this.experience.eventBus;
        this.camera = this.experience.camera;
        this.scene = this.experience.scene;
        this.deckLoader = this.experience.deckLoader;

        this.raycaster = new THREE.Raycaster();
        this.table = new Table();
        this.light = new Light();
        this.allCards = [];
        this.highlightedCard = null;
        this.selectedCard = null;
        this.focusedCard = null;
        this.nextSortOrder = 0;
        this.liftedCard = null;
        this.highLiftedCard = null;

        this.scrollBusy = false;
        this.focusDir = null;
        this.lastMousePos = null;

        this.allZones = [];
        this.zonesLayout = zonesLayout;
        if (zonesLayout) this.createZones(zonesLayout);

        this.eventBus.once('resourcesLoaded', this.onResourcedLoaded.bind(this));

        // Remote handlers are installed by the behavior via installRemoteHandlers()
        this._remoteRefs = [];
    }

    // ─── Behavior wiring ──────────────────────────────────────────────

    installRemoteHandlers(handlers) {
        this.destroyRemoteListeners();
        this._remoteRefs = Object.entries(handlers).map(([event, handler]) => ({
            event,
            ref: this.eventBus.onRemote(event, handler),
        }));
    }

    destroyRemoteListeners() {
        for (const { event, ref } of this._remoteRefs) {
            this.eventBus.offRemote(event, ref);
        }
        this._remoteRefs = [];
    }

    destroy() {
        this.destroyRemoteListeners();
        this.destroyZones();

        if (this.light) {
            this.light.destroy();
            this.light = null;
        }

        if (this.table) {
            this.table.destroy();
            this.table = null;
        }

        for (const card of this.allCards) {
            this.scene.remove(card.wrapper);
        }

        this.allCards = [];
        this.highlightedCard = null;
        this.selectedCard = null;
        this.focusedCard = null;
        this.liftedCard = null;
        this.highLiftedCard = null;
    }

    // ─── Card state events ────────────────────────────────────────────

    emitCardStateChanged() {
        this.eventBus.pushLocal('cardStateChanged');
    }

    // ─── Card primitives ──────────────────────────────────────────────

    selectNothing() {
        this.defocusAnyCard();
        this.deselectAnyCard();
    }

    deselectAnyCard() {
        if (!this.selectedCard) return;
        this.selectedCard.deselect();
        this.reorderCards(this.selectedCard);
        this.eventBus.pushRemote('deselectedCard', { id: this.selectedCard.id, index: this.selectedCard.sortOrder });
        this.selectedCard = null;
        this.emitCardStateChanged();
    }

    defocusAnyCard(onComplete) {
        if (!this.focusedCard) return;

        const card = this.focusedCard;

        if (this.selectedCard?.id === card.id && !card.isSelected) {
            card.select();
        }

        card.defocus(onComplete);
        this.reorderCards(card);
        this.eventBus.pushRemote('defocusedCard', { id: card.id, index: card.sortOrder });
        this.eventBus.pushLocal('defocusedCard', { id: card.id });
        this.focusedCard = null;
        this.emitCardStateChanged();
    }

    selectCard(cardOrId) {
        const card = this.getCard(cardOrId);
        card.select();
        this.selectedCard = card;
        this.eventBus.pushRemote('selectedCard', { id: card.id });
        this.emitCardStateChanged();
    }

    deselectCard(cardOrId) {
        const card = this.getCard(cardOrId);
        if (this.selectedCard && this.selectedCard.id !== card.id) {
            this.reorderCards(this.selectedCard);
            this.selectedCard.deselect();
        }
        if (card.isSelected) {
            this.reorderCards(card);
            card.deselect();
        }
        this.selectedCard = null;
        this.eventBus.pushRemote('deselectedCard', { id: card.id, index: card.sortOrder });
        this.emitCardStateChanged();
    }

    focusCard(cardOrId, onComplete) {
        const card = this.getCard(cardOrId);
        if (this.focusedCard && this.focusedCard.id !== card.id) this.focusedCard.defocus();
        this.removeHighlightCard();
        card.focus(onComplete);
        this.focusedCard = card;
        this.reorderCards(card);
        this.eventBus.pushRemote('focusedCard', { id: card.id });
        this.eventBus.pushLocal('focusedCard', { id: card.id });
        this.emitCardStateChanged();
    }

    removeHighlightCard() {
        if (!this.highlightedCard) return;
        this.highlightedCard.highlightOff();
        this.highlightedCard = null;
    }

    activateHighlightCard(cardOrId) {
        if (this.focusedCard) {
            this.removeHighlightCard()
            return;
        }

        const card = this.getCard(cardOrId);
        if (card.id === this.selectedCard?.id) {
            this.removeHighlightCard();
            return;
        }
        if (card.isHighlighted) return;
        this.removeHighlightCard();
        card.highlightOn();
        this.highlightedCard = card;
    }

    dragCard(cardOrId, targetPosition) {
        const card = this.getCard(cardOrId);
        card.dragTo(targetPosition);
        this.eventBus.pushRemote('draggedCard', { id: card.id, x: targetPosition.x, y: targetPosition.y });
    }

    beginCardMotion(cardOrId, options = {}) {
        const card = this.getCard(cardOrId);
        card.beginMotion(options);
        return card;
    }

    updateCardMotion(cardOrId, targetPosition, options = {}) {
        const card = this.getCard(cardOrId);
        card.updateMotionFrame(targetPosition, options);
        return card;
    }

    endCardMotion(cardOrId, finalPosition, options = {}) {
        const card = this.getCard(cardOrId);
        card.endMotion(finalPosition, options);
        return card;
    }

    animateCardMotion(cardOrId, targetPosition, options = {}) {
        const card = this.getCard(cardOrId);
        return card.animateMotionTo(targetPosition, options);
    }

    flipCard(cardOrId, onComplete) {
        const card = this.getCard(cardOrId);
        if (this.selectedCard && this.selectedCard.id !== card.id) {
            this.selectedCard.deselect();
        }
        this.selectedCard = null;
        this.reorderCards(card);
        card.flipFace(onComplete);
        if (card.isFaceUp) {
            this.highLiftedCard = card;
            this.eventBus.pushRemote('flippedCardUp', { id: card.id, index: card.sortOrder });
            this.eventBus.pushLocal('flippedCardUp', { id: card.id });
        } else {
            if (this.highLiftedCard?.id === card.id) this.highLiftedCard = null;
            this.eventBus.pushRemote('flippedCardDown', { id: card.id, index: card.sortOrder });
            this.eventBus.pushLocal('flippedCardDown', { id: card.id });
        }
        if (!this.focusedCard) {
            this.emitCardStateChanged();
        }
    }

    // ─── Raycasting ───────────────────────────────────────────────────

    intersectCard(position, wrappers) {
        const intersects = this.intersectWrappers(position, wrappers).sort((a, b) => b.object.userData.instance.index - a.object.userData.instance.index);
        for (const intersect of intersects) {
            const child = intersect.object;
            if (child instanceof THREE.Mesh && child.name === 'wrapper') {
                return child.userData.instance;
            }
        }
    }

    intersectInteractableCard(position) {
        return this.intersectCard(position, this.getAllInteractableWrappers());
    }

    intersectWrappers(position, wrappers) {
        wrappers = wrappers || this.getAllWrappers();
        this.updateRaycaster(position);
        return this.raycaster.intersectObjects(wrappers, false);
    }

    intersectTablePos(position) {
        this.updateRaycaster(position);
        const intersects = this.raycaster.intersectObject(this.table.getMesh());
        return intersects.length > 0 ? intersects[0].point : null;
    }

    updateRaycaster(position) {
        this.raycaster.setFromCamera(position, this.camera.instance);
    }

    // ─── Card loading & management ────────────────────────────────────

    onResourcedLoaded() {
        const startX = -Math.floor(this.deckLoader.cards.length / 2);
        const sorted = [...this.deckLoader.cards].sort((a, b) => a.index - b.index);
        this.nextSortOrder = sorted.length > 0 ? sorted[sorted.length - 1].index + 1 : 0;
        for (const card of sorted) {
            this.addCard(card, startX + this.allCards.length * 0.1);
        }

        this.applyCardSettings(this.experience.settings || {}, {});

        // Notify behavior that cards are ready
        if (this.experience.behavior) {
            this.experience.behavior.onSetup(this.allCards);
        }
    }

    addCard(card, startX) {
        if (this.allCards.find(_card => _card.id === card.id))
            throw new Error(`Card with id ${card.id} already exists!`);

        this.allCards.push(card);
        card.setIndex(this.allCards.length - 1);
        const pos = card.wrapper.position;
        if (pos.x != null || pos.y != null) {
            card.setPosition(new THREE.Vector2(pos.x, pos.y));
        } else {
            const index = card.index || this.allCards.length - 1;
            card.setPosition(new THREE.Vector2(index * -0.0005, 1.55 + index * 0.005));
        }
        card.liftUp(Space.CARD_DOWN_Z);
        this.liftedCard = null;
    }

    addCardDynamic(cardData) {
        const textureUrl = cardData.front?.texture || '/textures/blank_card.png';
        const backTextureUrl = cardData.back?.texture || '/textures/blank_card.png';
        const emissiveColor = this.experience.settings?.emissive_color
            ? new THREE.Color(this.experience.settings.emissive_color).getHex()
            : Card.DEFAULT_EMISSIVE_COLOR;

        const loader = new THREE.TextureLoader();
        loader.load(textureUrl, (texture) => {
            const card = Card.newFromSourceTexture(
                cardData.id,
                texture,
                cardData.x || 0,
                cardData.y || 0,
                cardData.z || 0.003,
                emissiveColor
            );
            if (cardData.face_up) card.faceUp();
            this.addCard(card);

            const loadOptionalMap = (path, apply) => {
                if (!path) return;
                loader.load(path, apply);
            };

            if (backTextureUrl) {
                loader.load(backTextureUrl, (backTexture) => {
                    card.setTexture(backTexture, Card.BACK_SIDE);
                });
            }

            loadOptionalMap(cardData.front?.metalness, (map) => {
                card.setMetalnessMap(map, Card.FRONT_SIDE);
            });

            loadOptionalMap(cardData.back?.metalness, (map) => {
                card.setMetalnessMap(map, Card.BACK_SIDE);
            });

            loadOptionalMap(cardData.front?.normal, (map) => {
                card.setNormalMap(map, Card.FRONT_SIDE);
            });

            loadOptionalMap(cardData.back?.normal, (map) => {
                card.setNormalMap(map, Card.BACK_SIDE);
            });
        });
    }

    update(delta) {}

    applyTextureSampling() {
        for (const card of this.allCards) {
            if (typeof card.refreshTextureSampling === 'function') {
                card.refreshTextureSampling();
            }
        }
    }

    applyCardSettings(settings = {}, previousSettings = {}) {
        const hasValue = (value) => value !== undefined && value !== null;

        if (
            hasValue(settings.emissive_color) &&
            settings.emissive_color !== previousSettings.emissive_color
        ) {
            for (const card of this.allCards) {
                if (typeof card.setEmissiveColor === 'function') {
                    card.setEmissiveColor(settings.emissive_color);
                }
            }
        }

        if (hasValue(settings.card_rotation_strength)) {
            for (const card of this.allCards) {
                card.rotationStrength = settings.card_rotation_strength;
            }
        }

        if (hasValue(settings.card_rotation_max)) {
            for (const card of this.allCards) {
                card.rotationMax = settings.card_rotation_max;
            }
        }
    }

    // ─── Card lookup & ordering ───────────────────────────────────────

    getCard(idOrCard) {
        if (idOrCard instanceof Card) return idOrCard;
        return this.getCardById(idOrCard);
    }

    getCardById(id) {
        const card = this.allCards.find(card => card.id === id);
        if (!card) throw new Error(`Card with id ${id} not found!`);
        return card;
    }

    getAllCards() {
        return this.allCards;
    }

    getAllWrappers() {
        return this.allCards.map(card => card.wrapper);
    }

    getAllInteractableWrappers() {
        return this.allCards.filter(card => card.isInteractable).map(card => card.wrapper);
    }

    reorderCards(card) {
        const reorderd = this.allCards
            .filter(_card => _card.id !== card.id)
            .sort((a, b) => a.index - b.index);
        reorderd.push(card)
        this.allCards = reorderd;
        this.allCards.forEach((card, index) => card.setIndex(index));
        card.sortOrder = this.nextSortOrder++;
    }

    // ─── Zones ────────────────────────────────────────────────────────

    getZoneAtPosition(position) {
        if (!position) return null;
        return this.allZones.find(zone => zone.containsPoint(position)) || null;
    }

    getZoneForCard(cardOrId) {
        const card = this.getCard(cardOrId);
        return this.getZoneAtPosition(card?.targetPosition || card?.position);
    }

    resize(data) {
        if (this.allZones.length > 0) {
            for (const zone of this.allZones) {
                if (zone.outlineStrength > 0) {
                    zone.updateOutlineScreenResolution(data);
                }
            }
        }
    }

    createZones(data) {
        for (let i = 0; i < data.zones.length; i++) {
            const source = data.zones[i];
            const zone = new Zone(
                source.id || `zone_${i + 1}`,
                source.title,
                source.x,
                source.y,
                source.width,
                source.height,
                source.color,
                source.bevel,
                source.border,
                source.font_size,
                source.font_color,
                source.font_color_contour,
            )
            this.allZones.push(zone);
        }
        this.placeZonesInRowsAndColumns()
    }

    placeZonesInRowsAndColumns() {
        if (!this.allZones || !this.zonesLayout) return;

        const tableZHeight = this.table.mesh.position.z;
        const position = new THREE.Vector3(0, 0, tableZHeight + 0.02);
        const zones = this.allZones.length;
        const rowsMax = this.zonesLayout.rows;
        const columnsMax = this.zonesLayout.columns;
        const spacingX = this.zonesLayout.spacing_x;
        const spacingY = this.zonesLayout.spacing_y;
        let offset = 0;
        let row = 0;
        let column = 0;
        for (let z = 0; z < zones; z++) {
            let individualPosX = this.allZones[z].x
            let individualPosY = this.allZones[z].y
            position.x = Space.ZONE_GROUP_POS.x + individualPosX + (column * spacingX + offset);
            position.y = Space.ZONE_GROUP_POS.y + individualPosY + (row * spacingY * -1);
            this.allZones[z].place(position)

            column++;
            if (column % columnsMax === 0) {
                row++
                column = 0;
                if (offset > 0) {
                    offset = 0
                } else {
                    offset = spacingX / 2;
                }
            }
        }
    }

    destroyZones() {
        if (!this.allZones.length) return;

        for (const zone of this.allZones) {
            zone.zone.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    child.geometry.dispose();
                    if (child.material) {
                        if (child.material.map) child.material.map.dispose();
                        child.material.dispose();
                    }
                }
            });
            this.scene.remove(zone.zone);
        }
        this.allZones = [];
    }

    resetZones(newLayout) {
        this.destroyZones();
        this.zonesLayout = newLayout;
        if (newLayout) {
            this.createZones(newLayout);
        }
    }
}
